import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useEmployeePrerequisite } from '../../../case-management/cases/useEmployeePrerequisite';
import { evidenceCasesApi } from '../evidenceApi';
import { CASE_STATUS_LABEL, type EvidenceCaseListItem } from '../evidenceTypes';

const EMPLOYEE_DETAIL =
  "Evidence Management is open only to employees, and your account is not linked to an employee's personnel record — so you cannot receive evidence or be added to a case. Link it on the personnel record's Account card, or ask HR to.";

/**
 * The Evidence work surface — every case the reader is IN, each opened on its evidence.
 *
 * <b>Evidence registers no case.</b> A case is registered, edited, classified, shared and deleted
 * in Case Management (product call, 12 Oct 2026); this list reads the same registry under the
 * same answer, so a case appears here the moment its team is given it there. The row's menu
 * links to that record rather than repeating its controls.
 *
 * <b>A case is private, so this is not every case.</b> A case nobody shared with the reader is not
 * listed at all. A case they ARE in but whose access level is above their clearance is listed,
 * counted and MASKED — the title is asterisks, fixed length, and the server does not send it.
 */
export function EvidenceCasesIndexPage() {
  const navigate = useNavigate();

  // The employee rule, stated before the reader meets the refusal on a case's own surfaces.
  const employee = useEmployeePrerequisite(evidenceCasesApi.eligibility, EMPLOYEE_DETAIL);

  const fetchCases = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      evidenceCasesApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          caseStatus: query.facets.caseStatus?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (record: EvidenceCaseListItem): MenuItem[] => [
      {
        key: `case-${record.id}`,
        label: 'Open case record',
        permission: 'cases.read',
        disabledReason: record.isRestricted ? 'This case is above your access level.' : null,
        onClick: () => navigate(`/cases/${record.id}`),
      },
    ],
    [navigate],
  );

  return (
    <IndexPage<EvidenceCaseListItem>
      module="evidence-items"
      moduleNoun="cases"
      breadcrumb={['Evidence Management', 'Evidence']}
      columns={COLUMNS}
      facets={[
        { key: 'status', label: 'Status' },
        { key: 'caseStatus', label: 'Case status' },
      ]}
      auditEntityTypes={EVIDENCE_AUDIT_ENTITY_TYPES}
      rowKey={(record) => record.id}
      fetch={fetchCases}
      searchPlaceholder="Search case number or title"
      emptyMessage="No cases yet — or none shared with you. A case is registered in Case Management and is visible only to the people it is shared with."
      prerequisites={[employee]}
      onRowOpen={(record) => navigate(`/evidence/cases/${record.id}`)}
      openPermission="evidence-items.read"
      rowActions={rowActions}
      otherInfo={(record) => record}
    />
  );
}

/**
 * What the Evidence page's History Logs read — the records this module itself writes. The case's
 * own writes are Case Management's history now.
 */
const EVIDENCE_AUDIT_ENTITY_TYPES = [
  'EvidenceItem',
  'EvidenceReceiving',
  'EvidenceMovementRequest',
  'EvidenceTransferRequest',
  'EvidenceAcknowledgementReceipt',
];

const COLUMNS: ColumnDef<EvidenceCaseListItem>[] = [
  /*
    THE CASE NUMBER — TITLE, one anchor — product call, 09 Oct 2026: *concat the title on the case id*. The
    anchor-joins-two-values exception: one line, one header; only the leading CODE is mono. A restricted
    case keeps its fixed mask and the screen-reader sentence saying why.
  */
  {
    key: 'caseNumber',
    header: 'Case',
    width: 420,
    flex: true,
    sortable: true,
    summary: true,
    render: (record) =>
      record.isRestricted ? (
        <span className="truncate">
          <span className="font-mono">{record.caseNumber}</span>
          {' — '}
          <span aria-hidden="true" className="font-mono text-slate-400">
            {record.title}
          </span>
          <span className="sr-only">Title withheld — above your access level</span>
        </span>
      ) : (
        <TextCell code={record.caseNumber} value={record.title} />
      ),
  },
  {
    key: 'accessLevel',
    header: 'Access level',
    width: 150,
    sortable: true,
    summary: true,
    render: (record) => <TextCell value={record.accessLevelName ?? 'Unclassified'} />,
  },
  {
    key: 'itemCount',
    header: 'Evidence',
    width: 100,
    align: 'right',
    sortable: true,
    render: (record) =>
      record.isRestricted ? (
        <TextCell value={null} />
      ) : (
        <TextCell value={String(record.itemCount)} mono numeric />
      ),
  },
  {
    key: 'caseStatus',
    header: 'Case status',
    width: 140,
    stage: true,
    render: (record) => <StatusText tone="inactive">{CASE_STATUS_LABEL[record.status]}</StatusText>,
  },
  {
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (record) =>
      record.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
  },
];
